import { useEffect, useState } from "react"
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"
import API from "@/common/API"
import { Routes } from "@/routes"
import UsageStatisticsComponent from "@/components/platform/UsageStatisticsComponent"
import VisitReviewComponent from "@/components/platform/VisitReviewComponent"
import SyncMonitorComponent from "@/components/platform/SyncMonitorComponent"
import IndicatorsPage from "@/pages/indicators/IndicatorsPage"
import ActivityLogPage from "@/pages/ActivityLogPage"
import VehicleStopsPage from "@/pages/VehicleStopsPage"
import DispatchIndicatorsPage from "@/pages/indicators/DispatchIndicatorsPage"

interface PlatformStats {
  totalVisits: number
  activeAgents: number
  totalStops: number
  placesMatched: number
}

interface OrgRecord {
  id: string
  name: string
  active: boolean
  totalPlaces: number
  totalUsers: number
  totalVehicles: number
}

type Tab = "overview" | "organizations" | "vehicleStops" | "activityLog" | "usage" | "visitReview" | "sync" | "dispatch"

const PlatformPage = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const user = parsedUser()

  const [activeTab, setActiveTab] = useState<Tab>("overview")
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [orgs, setOrgs] = useState<OrgRecord[]>([])
  const [orgsPage, setOrgsPage] = useState(0)
  const [orgsTotalPages, setOrgsTotalPages] = useState(0)
  const [orgsSearch, setOrgsSearch] = useState("")
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)

  // Guard — redirect non-superadmin users
  useEffect(() => {
    if (!isSuperAdmin(user)) {
      history.replace(Routes.Home.path)
    }
  }, [])

  const loadStats = async () => {
    try {
      setIsLoadingStats(true)
      const res = await API.getAction("Platform/Stats")
      if (res.data.status === "ok") {
        setStats(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const loadOrgs = async (page = 0, search = orgsSearch) => {
    try {
      setIsLoadingOrgs(true)
      const qs = `Page=${page}&Quantity=20${search ? `&Search=${encodeURIComponent(search)}` : ""}`
      const res = await API.getAction("Organization/AdminList", qs)
      if (res.data.status === "ok") {
        setOrgs(res.data.response)
        setOrgsTotalPages(res.data.quantityOfPages)
        setOrgsPage(page)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrgs(false)
    }
  }

  useEffect(() => {
    loadStats()
    loadOrgs()
  }, [])

  const kpiCards = [
    { label: t("Total Visits"),   value: stats?.totalVisits,   icon: "fa-map-marker-alt", color: "primary" },
    { label: t("Active Agents"),  value: stats?.activeAgents,  icon: "fa-users",          color: "success" },
    { label: t("Vehicle Stops"),  value: stats?.totalStops,    icon: "fa-map-pin",        color: "warning" },
    { label: t("Places Matched"), value: stats?.placesMatched, icon: "fa-check-circle",   color: "info"    },
  ]

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: "overview",      label: t("Overview"),         icon: "fa-gauge-high" },
    { key: "organizations", label: t("Organizations"),    icon: "fa-building"   },
    { key: "vehicleStops",  label: t("Vehicle Stops"),    icon: "fa-map-pin"    },
    { key: "activityLog",   label: t("Activity Log"),     icon: "fa-clock-rotate-left" },
    { key: "usage",         label: t("Usage Statistics"), icon: "fa-chart-bar"  },
    { key: "visitReview",   label: t("Visit Review"),     icon: "fa-route"      },
    { key: "sync",          label: t("Sync Center"),      icon: "fa-rotate"     },
    { key: "dispatch",      label: t("Dispatch"),         icon: "fa-truck-ramp-box" },
  ]

  return (
    <Container fluid className="px-4 py-3">
      {/* Header */}
      <div className="d-flex align-items-center mb-4 gap-2">
        <i className="fas fa-shield-halved fa-lg text-warning" />
        <h4 className="mb-0 fw-bold">{t("Platform Admin")}</h4>
        <Badge bg="warning" text="dark" className="ms-1">superadmin</Badge>
      </div>

      {/* Tab nav */}
      <ul className="nav nav-tabs mb-4">
        {tabs.map(tab => (
          <li className="nav-item" key={tab.key}>
            <button
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`fas ${tab.icon} me-2`} />
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ─── OVERVIEW TAB ─── */}
      {activeTab === "overview" && (
        <>
          {/* KPI cards */}
          <Row className="g-3 mb-4">
            {kpiCards.map((card, i) => (
              <Col xs={6} md={3} key={i}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="d-flex align-items-center gap-3">
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center bg-${card.color} bg-opacity-10`}
                      style={{ width: 48, height: 48, flexShrink: 0 }}
                    >
                      <i className={`fa ${card.icon} text-${card.color}`} />
                    </div>
                    <div>
                      <div className="text-muted small">{card.label}</div>
                      {isLoadingStats ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <div className="fw-bold fs-5">{card.value?.toLocaleString() ?? "—"}</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <IndicatorsPage embedded />
            </Card.Body>
          </Card>
        </>
      )}

      {activeTab === "vehicleStops" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <VehicleStopsPage embedded />
          </Card.Body>
        </Card>
      )}

      {activeTab === "activityLog" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <ActivityLogPage embedded />
          </Card.Body>
        </Card>
      )}

      {activeTab === "usage" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <UsageStatisticsComponent />
          </Card.Body>
        </Card>
      )}

      {activeTab === "visitReview" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <VisitReviewComponent />
          </Card.Body>
        </Card>
      )}

      {activeTab === "sync" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <SyncMonitorComponent />
          </Card.Body>
        </Card>
      )}

      {activeTab === "dispatch" && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <DispatchIndicatorsPage embedded />
          </Card.Body>
        </Card>
      )}

      {/* ─── ORGANIZATIONS TAB ─── */}
      {activeTab === "organizations" && (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              <i className="fa fa-building me-2 text-muted" />
              {t("Organizations")}
            </span>
            <Form.Control
              size="sm"
              placeholder={t("Search...")}
              style={{ maxWidth: 220 }}
              value={orgsSearch}
              onChange={e => setOrgsSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") loadOrgs(0, orgsSearch) }}
            />
          </Card.Header>
          <Card.Body className="p-0">
            {isLoadingOrgs ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
            ) : (
              <Table hover responsive className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("Name")}</th>
                    <th className="text-center">{t("Places")}</th>
                    <th className="text-center">{t("Users")}</th>
                    <th className="text-center">{t("Vehicles")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        {t("No organizations found")}
                      </td>
                    </tr>
                  ) : orgs.map(org => (
                    <tr key={org.id}>
                      <td className="fw-semibold">{org.name}</td>
                      <td className="text-center"><Badge bg="light" text="dark">{org.totalPlaces}</Badge></td>
                      <td className="text-center"><Badge bg="light" text="dark">{org.totalUsers}</Badge></td>
                      <td className="text-center"><Badge bg="light" text="dark">{org.totalVehicles}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
          {orgsTotalPages > 1 && (
            <Card.Footer className="bg-white d-flex justify-content-between align-items-center">
              <Button variant="outline-secondary" size="sm"
                disabled={orgsPage === 0} onClick={() => loadOrgs(orgsPage - 1)}>
                <i className="fa fa-chevron-left" />
              </Button>
              <span className="small text-muted">
                {t("Page")} {orgsPage + 1} / {orgsTotalPages}
              </span>
              <Button variant="outline-secondary" size="sm"
                disabled={orgsPage + 1 >= orgsTotalPages} onClick={() => loadOrgs(orgsPage + 1)}>
                <i className="fa fa-chevron-right" />
              </Button>
            </Card.Footer>
          )}
        </Card>
      )}
    </Container>
  )
}

export default PlatformPage
